/**
 * SvgFigureEditor — 브라우저 SVG 그림 편집기(계약 BE-20261015 §6-1). 시제품 editor.html 의 이식 + 그리기 도구.
 *
 * 구조: SVG DOM 은 `mountRef` 컨테이너 안에서 **명령형**으로 다룬다 — `svgPolicy.checkSvg` 가 정제해 준 root 를
 * `document.importNode` 로 마운트하고(원문을 innerHTML 에 넣는 일은 절대 없다), 편집은 그 DOM 을 직접 고친 뒤
 * 직렬화 스냅샷을 이력(`EditorHistory`)에 쌓는다. 툴바·인스펙터·요소 목록은 React 상태로 그리며, DOM 이 바뀔 때마다
 * `tick` 을 올려 다시 읽는다. 선택 테두리·핸들은 편집기 UI(div) 라 저장본에 들어가지 않는다(id 접두 `svg-editor-` 예약).
 *
 * 산출물은 svgPolicy 허용 요소·속성만 — 그리기 도구는 editorTools 가 만들고, `getSvgText()` 는 직렬화 뒤
 * `checkSvg` 를 한 번 더 통과시킨 결과를 돌려준다(못 통과하면 onInvalid + 마지막 유효본).
 */
import React, {
  forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState,
} from 'react';
import { checkSvg, serializeSvg, parserError, MAX_BYTES } from './svgPolicy';
import { utf8ByteLength } from './figureSvg';
import SvgFigurePreview from './SvgFigurePreview';
import { ensureFigureFontStyle } from './figureFont';
import { EditorHistory } from './editorHistory';
import {
  apply, invert, fmt, composeTranslate, composeRotate, composeScale, snap, snapPoint, screenMatrix, toUserPoint, screenBox,
} from './editorTransform';
import {
  TOOLS, CLICK_TOOLS, SELECTABLE, DASH_PRESETS, LINECAPS, ANCHORS, SWATCHES, GRID_DIVISIONS, TEXT_SIZE_RATIO,
  descendants, viewBoxOf, gridOf, listSelectable, selectableTarget, allocId, make, ensureArrowMarker, defaultStrokeWidth,
  pointsAttr, pathData, simplify, textRuns, inheritedAttr, dasharrayFor, presetOf, cloneWithFreshIds, firstDrawable,
} from './editorTools';
import './svgEditor.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');
const HANDLES = Object.freeze([
  ['nw', 'svg-editor-handle svg-editor-handle--nw'],
  ['ne', 'svg-editor-handle svg-editor-handle--ne'],
  ['sw', 'svg-editor-handle svg-editor-handle--sw'],
  ['se', 'svg-editor-handle svg-editor-handle--se'],
  ['rotate', 'svg-editor-handle svg-editor-handle--rotate'],
]);
const ARROWS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
const MIN_SCALE = 0.05;
const DRAG_THRESHOLD = 2;   // px — 이보다 덜 움직인 클릭은 이동으로 치지 않는다
const ROTATE_STEP = 15;
const TEXT_PLACEHOLDER = '글자';
const STAGE_LABEL = 'SVG 편집 캔버스. 요소를 클릭해 고르고 드래그하거나 방향키로 옮깁니다. Shift 와 방향키는 10 단위, Delete 는 삭제, Ctrl+Z 는 실행 취소, Esc 는 선택 해제입니다.';
const RESIZE_HINT = '회전된 그룹 안의 요소는 크기 조절 대신 그룹을 선택하세요';
const REMOUNT_FAIL = '이 단계의 스냅샷을 다시 읽을 수 없어 캔버스를 바꾸지 않았습니다';
// XML 1.0 이 금지하는 제어문자(탭·줄바꿈·CR 제외) — 글자 입력에 섞이면 직렬화 스냅샷을 다시 파싱할 수 없다
// eslint-disable-next-line no-control-regex
const XML_CONTROL = /[\x00-\x08\x0B\x0C\x0E-\x1F]/g;
const AXIS_EPS = 1e-6;

const isConnected = (el) => el.isConnected;
/** 부모 CTM 에 회전·기울임(b≠0 ‖ c≠0)이 있으면 모서리 핸들의 축이 화면과 어긋난다 — 그런 선택은 크기 조절을 막는다(CTM 을 못 읽으면 막지 않음). */
const axisAligned = (m) => !m || (Math.abs(m.b) < AXIS_EPS && Math.abs(m.c) < AXIS_EPS);
const canResize = (els) => els.every((el) => axisAligned(screenMatrix(el.parentNode)));
/** 조상이 같이 선택된 요소는 뺀다(그룹과 그 자식을 함께 옮기면 두 번 움직인다). */
const normalize = (els) => els.filter((el) => !els.some((o) => o !== el && o.contains(el)));
const setTransform = (el, value) => (value ? el.setAttribute('transform', value) : el.removeAttribute('transform'));

function rowLabel(el) {
  const id = el.getAttribute('id');
  const text = el.localName === 'text' ? el.textContent.replace(/\s+/g, ' ').trim().slice(0, 24) : '';
  return `${el.localName === 'g' ? '그룹' : el.localName}${id ? ` #${id}` : ''}${text ? ` · ${text}` : ''}`;
}

/** 다시 마운트할 때 선택을 잇기 위한 열쇠 — id 가 있으면 id, 없으면 문서 순서 번호. */
function selectionKeys(root, els) {
  const all = descendants(root);
  return els.map((el) => ({ id: el.getAttribute('id'), index: all.indexOf(el) }));
}
function resolveKeys(root, keys) {
  const all = descendants(root);
  return keys.map((k) => {
    const byId = k.id ? all.find((el) => el.getAttribute('id') === k.id) : null;
    const byIndex = all[k.index];
    return byId || (byIndex && SELECTABLE.has(byIndex.localName) ? byIndex : null);
  }).filter(Boolean);
}

const SvgFigureEditor = forwardRef(function SvgFigureEditor({
  svgText, readOnly = false, maxBytes = MAX_BYTES, onChange, onInvalid,
}, ref) {
  const uid = useId();
  const stageRef = useRef(null);
  const mountRef = useRef(null);
  const overlayRef = useRef(null);
  const inspectorRef = useRef(null);
  const angleRef = useRef(null);
  const rootRef = useRef(null);          // 마운트된 <svg>
  const historyRef = useRef(null);       // EditorHistory
  const selectionRef = useRef([]);       // 선택된 요소들(Element[])
  const gestureRef = useRef(null);       // 진행 중인 포인터 제스처(move/rotate/scale/draw)
  const draftRef = useRef(null);         // 클릭형 그리기(polygon/polyline) 진행 상태
  const lastValidRef = useRef('');
  const pendingFocusRef = useRef(false);
  // 인스펙터 입력(비제어 defaultValue)을 다시 그릴 때만 바꾼다: 선택이 바뀌거나 DOM 을 다시 마운트했을 때. 편집마다 바꾸면
  // 입력의 blur 커밋이 옆 버튼의 mousedown~click 사이에 fieldset 을 갈아 끼워 그 클릭이 사라진다.
  const selectGenRef = useRef(0);
  const reloadGenRef = useRef(0);
  const callbacksRef = useRef({});
  callbacksRef.current = { onChange, onInvalid };

  const [, setTick] = useState(0);   // DOM 이 바뀌면 올려서 다시 읽는다(값 자체는 안 쓴다)
  const [tool, setTool] = useState('select');
  const [gridSnap, setGridSnap] = useState(false);
  const [angleSnap, setAngleSnap] = useState(true);
  const [invalid, setInvalid] = useState(null);
  const [notice, setNotice] = useState('');
  const bump = useCallback(() => setTick((t) => t + 1), []);
  const locked = readOnly || !!invalid;

  // ── 상태 읽기·쓰기 ────────────────────────────────────────────────
  const serialize = () => serializeSvg(rootRef.current);
  const select = (els) => { selectionRef.current = els; selectGenRef.current += 1; bump(); };
  const selected = () => normalize(selectionRef.current.filter(isConnected));

  const emitChange = () => {
    const h = historyRef.current;
    if (h && callbacksRef.current.onChange) {
      callbacksRef.current.onChange(h.current, { dirty: h.dirty, bytes: utf8ByteLength(h.current) });
    }
  };
  /** 편집이 끝날 때마다: 저장 전 검사를 미리 돌려 마지막 유효본을 갱신하고, 못 통과하면(상한 초과 등) 사유를 보인다. */
  const validate = (text) => {
    const r = checkSvg(text, { maxBytes });
    if (r.ok) { lastValidRef.current = r.svg; setNotice(''); } else setNotice(`${r.reason} (${r.code})`);
  };
  /** 편집 한 번 = 이력 한 칸. before 와 같으면 아무것도 안 한다. */
  const commit = (before) => {
    const h = historyRef.current;
    if (!h) return false;
    const after = serialize();
    if (!h.push(after) && after === before) return false;
    validate(after);
    emitChange();
    bump();
    return true;
  };
  /** 이력 스냅샷(우리 DOM 의 직렬화본)을 다시 마운트 — 선택은 id·문서 순서로 잇는다. 스냅샷이 비정형이면(제어문자 등) 캔버스를 두고 사유만. */
  const remountFrom = useCallback((text) => {
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    if (parserError(doc)) { setNotice(REMOUNT_FAIL); return; }
    const mount = mountRef.current;
    const old = rootRef.current;
    const keys = old ? selectionKeys(old, selectionRef.current.filter(isConnected)) : [];
    const root = document.importNode(doc.documentElement, true);
    while (mount.firstChild) mount.removeChild(mount.firstChild);
    mount.appendChild(root);
    rootRef.current = root;
    selectionRef.current = resolveKeys(root, keys);
    reloadGenRef.current += 1;
  }, []);
  /** prop svgText 적재 — 정책을 못 통과하면 읽기 전용(미리보기만) + onInvalid. */
  const load = useCallback((text) => {
    gestureRef.current = null;
    draftRef.current = null;
    selectionRef.current = [];
    reloadGenRef.current += 1;
    const mount = mountRef.current;
    while (mount.firstChild) mount.removeChild(mount.firstChild);
    const r = checkSvg(text, { maxBytes });
    if (!r.ok) {
      rootRef.current = null;
      historyRef.current = null;
      lastValidRef.current = '';
      setInvalid({ code: r.code, reason: r.reason });
      if (callbacksRef.current.onInvalid) callbacksRef.current.onInvalid({ code: r.code, reason: r.reason });
    } else {
      const root = document.importNode(r.root, true);
      mount.appendChild(root);
      rootRef.current = root;
      historyRef.current = new EditorHistory(r.svg);
      lastValidRef.current = r.svg;
      setInvalid(null);
    }
    setNotice('');
    setTool('select');
    bump();
  }, [maxBytes, bump]);
  // 캔버스 글꼴 = <img> 글꼴(KyMath, BE-20261019). 페이지 CSS 로만 걸고 SVG DOM 은 안 건드려 serialize·이력·저장본에 안 섞인다.
  useLayoutEffect(() => { ensureFigureFontStyle('.svg-editor-mount '); }, []);
  useLayoutEffect(() => { load(svgText); }, [svgText, load]);

  // ── 선택 테두리(화면 좌표, 편집기 UI 전용) ────────────────────────
  const refreshOverlay = useCallback(() => {
    const overlay = overlayRef.current;
    const stage = stageRef.current;
    if (!overlay || !stage) return;
    const box = screenBox(selectionRef.current.filter(isConnected));
    if (!box) { overlay.style.display = 'none'; return; }
    const host = stage.getBoundingClientRect();
    overlay.style.display = 'block';
    overlay.style.left = `${box.x - host.left - stage.clientLeft - 4}px`;
    overlay.style.top = `${box.y - host.top - stage.clientTop - 4}px`;
    overlay.style.width = `${box.width + 8}px`;
    overlay.style.height = `${box.height + 8}px`;
  }, []);
  useLayoutEffect(() => { refreshOverlay(); });
  useEffect(() => {
    window.addEventListener('resize', refreshOverlay);
    window.addEventListener('scroll', refreshOverlay, true);
    const ro = typeof ResizeObserver === 'function' && stageRef.current ? new ResizeObserver(refreshOverlay) : null;
    if (ro) ro.observe(stageRef.current);
    return () => {
      window.removeEventListener('resize', refreshOverlay);
      window.removeEventListener('scroll', refreshOverlay, true);
      if (ro) ro.disconnect();
    };
  }, [refreshOverlay]);
  useEffect(() => {   // 글자 도구로 만든 직후 인스펙터의 글자 입력으로 포커스
    if (!pendingFocusRef.current) return;
    pendingFocusRef.current = false;
    const input = inspectorRef.current && inspectorRef.current.querySelector('input[data-run]');
    if (input) { input.focus(); input.select(); }
  });

  // ── 이력·공개 메서드 ──────────────────────────────────────────────
  const cancelGesture = () => {
    const g = gestureRef.current;
    if (!g) return;
    gestureRef.current = null;
    if (g.kind === 'draw') remountFrom(g.before);
    else for (const it of g.items) setTransform(it.el, it.original);
    refreshOverlay();
  };
  const cancelDraft = () => {
    const d = draftRef.current;
    if (!d) return;
    draftRef.current = null;
    remountFrom(d.before);
    bump();
  };
  const undo = () => {
    const h = historyRef.current;
    if (!h || !h.canUndo()) return;
    cancelGesture();
    cancelDraft();
    remountFrom(h.undo());
    emitChange();
    bump();
  };
  const redo = () => {
    const h = historyRef.current;
    if (!h || !h.canRedo()) return;
    cancelGesture();
    cancelDraft();
    remountFrom(h.redo());
    emitChange();
    bump();
  };
  const reset = () => {
    const h = historyRef.current;
    if (!h || !h.dirty) return;
    cancelGesture();
    cancelDraft();
    remountFrom(h.original);
    h.push(h.original);   // 실행 취소로 편집본을 되돌릴 수 있게 이력 한 칸으로
    emitChange();
    bump();
  };
  const selectById = (id) => {
    const root = rootRef.current;
    if (!root) return false;
    let el = descendants(root).find((e) => e !== root && e.getAttribute('id') === id) || null;
    if (el && el.localName === 'tspan') el = el.closest('text');
    if (el && !listSelectable(root).some((row) => row.el === el)) el = null;
    select(el ? [el] : []);
    return !!el;
  };
  const getSvgText = () => {
    if (!rootRef.current) return '';
    const r = checkSvg(serialize(), { maxBytes });
    if (r.ok) { lastValidRef.current = r.svg; return r.svg; }
    if (callbacksRef.current.onInvalid) callbacksRef.current.onInvalid({ code: r.code, reason: r.reason });
    setNotice(`${r.reason} (${r.code})`);
    return lastValidRef.current;
  };
  useImperativeHandle(ref, () => ({
    getSvgText,
    reset,
    undo,
    redo,
    canUndo: () => !!(historyRef.current && historyRef.current.canUndo()),
    canRedo: () => !!(historyRef.current && historyRef.current.canRedo()),
    selectById,
  }));

  // ── 선택 요소 편집(이력 한 칸) ────────────────────────────────────
  const mutate = (fn) => {
    const els = selected();
    if (!els.length || !rootRef.current) return;
    const before = serialize();
    fn(els);
    commit(before);
  };
  const nudge = (dx, dy) => mutate((els) => {
    for (const el of els) setTransform(el, composeTranslate(el.getAttribute('transform') || '', dx, dy));
  });
  const deleteSelection = () => {
    mutate((els) => { for (const el of els) el.remove(); });
    select([]);
  };
  const duplicateSelection = () => {
    const root = rootRef.current;
    const grid = gridOf(root);
    let clones = [];
    mutate((els) => {
      clones = els.map((el) => {
        const clone = cloneWithFreshIds(root, el);
        setTransform(clone, composeTranslate(clone.getAttribute('transform') || '', grid, grid));
        el.parentNode.insertBefore(clone, el.nextSibling);
        return clone;
      });
    });
    if (clones.length) select(clones);
  };
  const reorder = (where) => mutate((els) => {
    for (const el of els) {
      const parent = el.parentNode;
      if (where === 'front') parent.appendChild(el);
      else parent.insertBefore(el, firstDrawable(parent));
    }
  });
  const setAttr = (name, value) => mutate((els) => {
    for (const el of els) {
      if (value === null || value === '') el.removeAttribute(name);
      else el.setAttribute(name, value);
    }
  });
  /** 숫자 입력 — 비우면 속성 제거(상속), 범위 밖이면 사유만. unit 과 같은 값은 제거(기본값과 같음). */
  const setNumberAttr = (name, raw, min, unit = null) => {
    const t = String(raw).trim();
    if (!t) { setAttr(name, null); return; }
    const v = Number(t);
    if (!Number.isFinite(v) || v < min) { setNotice(`${name} 은 ${min} 이상의 숫자로 입력하세요`); bump(); return; }
    setAttr(name, fmt(v) === unit ? null : fmt(v));
  };
  const setDash = (preset) => {
    if (preset === 'custom') return;
    mutate((els) => {
      for (const el of els) {
        if (preset !== 'solid') {
          el.setAttribute('stroke-dasharray', dasharrayFor(preset, inheritedAttr(el, rootRef.current, 'stroke-width')));
        } else if (inheritedAttr(el.parentNode, rootRef.current, 'stroke-dasharray')) {
          el.setAttribute('stroke-dasharray', 'none');   // 조상이 점선이면 none 으로 덮어야 실선이 된다
        } else {
          el.removeAttribute('stroke-dasharray');
        }
      }
    });
  };
  const setTextRun = (node, raw) => {
    const value = raw.replace(XML_CONTROL, '');   // XML 금지 제어문자는 지운다 — 들어가면 스냅샷을 다시 읽을 수 없다
    if (!node.isConnected || node.nodeValue === value) return;
    const before = serialize();
    node.nodeValue = value;
    commit(before);
  };
  /** 선택 중심(화면 경계 상자의 가운데)을 각 요소의 부모 좌표로 옮겨 회전을 합성. */
  const rotateBy = (deg) => {
    if (!Number.isFinite(deg) || !deg) return;
    const els = selected();
    if (!els.length) return;
    const box = screenBox(els);
    if (!box) { setNotice('회전 중심을 계산할 수 없습니다'); bump(); return; }
    const center = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    mutate((list) => {
      for (const el of list) {
        const m = screenMatrix(el.parentNode);
        const inv = m && invert(m);
        if (!inv) continue;
        const c = apply(inv, center.x, center.y);
        setTransform(el, composeRotate(el.getAttribute('transform') || '', deg, c.x, c.y));
      }
    });
    if (angleRef.current) angleRef.current.value = '0';
  };
  const chooseTool = (id) => {
    if (locked && id !== 'select') return;
    cancelGesture();
    cancelDraft();
    setTool(id);
    if (stageRef.current) stageRef.current.focus({ preventScroll: true });
  };

  // ── 포인터 제스처: 이동·회전·크기·그리기 ──────────────────────────
  const capture = (e) => {
    const s = stageRef.current;
    if (s && typeof s.setPointerCapture === 'function' && e.pointerId !== undefined) {
      try { s.setPointerCapture(e.pointerId); } catch (err) { /* 캡처가 안 돼도 동작엔 지장 없다 */ }
    }
  };
  const release = (e) => {
    const s = stageRef.current;
    if (s && typeof s.hasPointerCapture === 'function' && e.pointerId !== undefined && s.hasPointerCapture(e.pointerId)) {
      s.releasePointerCapture(e.pointerId);
    }
  };
  const samePointer = (g, e) => g.pointerId === undefined || e.pointerId === undefined || g.pointerId === e.pointerId;
  const userPoint = (e, withSnap) => {
    const root = rootRef.current;
    const p = toUserPoint(root, e.clientX, e.clientY);
    return withSnap && gridSnap ? snapPoint(p, gridOf(root)) : p;
  };

  const startMove = (e, els) => {
    const items = [];
    for (const el of normalize(els)) {
      const m = screenMatrix(el.parentNode);
      const inv = m && invert(m);
      if (inv) items.push({ el, original: el.getAttribute('transform') || '', inv, start: apply(inv, e.clientX, e.clientY) });
    }
    if (!items.length) { setNotice('좌표를 계산할 수 없습니다'); return; }
    gestureRef.current = { kind: 'move', pointerId: e.pointerId, before: serialize(), items, startX: e.clientX, startY: e.clientY, moved: false };
    capture(e);
  };
  const startHandle = (kind, e) => {
    const els = selected();
    const box = screenBox(els);
    if (!els.length || !box) { setNotice('선택 영역을 계산할 수 없습니다'); return; }
    const before = serialize();
    const base = { pointerId: e.pointerId, before, startX: e.clientX, startY: e.clientY, moved: false };
    const toParent = (p) => (el) => {
      const m = screenMatrix(el.parentNode);
      const inv = m && invert(m);
      return inv ? { el, original: el.getAttribute('transform') || '', point: apply(inv, p.x, p.y) } : null;
    };
    if (kind === 'rotate') {
      const center = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
      gestureRef.current = {
        ...base, kind: 'rotate', center, items: els.map(toParent(center)).filter(Boolean),
        startAngle: Math.atan2(e.clientY - center.y, e.clientX - center.x),
      };
    } else {
      const anchor = { x: kind.includes('w') ? box.x + box.width : box.x, y: kind.includes('n') ? box.y + box.height : box.y };   // 반대 모서리
      gestureRef.current = { ...base, kind: 'scale', anchor, items: els.map(toParent(anchor)).filter(Boolean) };
    }
    capture(e);
  };
  const startDraw = (e) => {
    const root = rootRef.current;
    const p = userPoint(e, tool !== 'freehand');
    if (!p) { setNotice('좌표를 계산할 수 없습니다'); return; }
    if (tool === 'text') { addText(p); return; }
    if (CLICK_TOOLS.has(tool)) { addVertex(p); return; }
    const before = serialize();
    const id = allocId(root);
    const stroke = { stroke: 'currentColor', 'stroke-width': fmt(defaultStrokeWidth(root)) };
    let el;
    if (tool === 'line') {
      el = make('line', { id, x1: fmt(p.x), y1: fmt(p.y), x2: fmt(p.x), y2: fmt(p.y), ...stroke, 'stroke-linecap': 'round' });
    } else if (tool === 'arrow') {
      el = make('line', { id, x1: fmt(p.x), y1: fmt(p.y), x2: fmt(p.x), y2: fmt(p.y), ...stroke, 'marker-end': `url(#${ensureArrowMarker(root)})` });
    } else if (tool === 'rect') {
      el = make('rect', { id, x: fmt(p.x), y: fmt(p.y), width: 0, height: 0, fill: 'none', ...stroke });
    } else if (tool === 'ellipse') {
      el = make('ellipse', { id, cx: fmt(p.x), cy: fmt(p.y), rx: 0, ry: 0, fill: 'none', ...stroke });
    } else {
      el = make('path', { id, d: pathData([p]), fill: 'none', ...stroke, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
    }
    root.appendChild(el);
    gestureRef.current = { kind: 'draw', tool, el, p0: p, points: [p], before, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, moved: false, shift: false };
    capture(e);
  };
  const addText = (p) => {
    const root = rootRef.current;
    const before = serialize();
    const el = make('text', { id: allocId(root), x: fmt(p.x), y: fmt(p.y), 'font-size': fmt(viewBoxOf(root)[2] * TEXT_SIZE_RATIO), 'text-anchor': 'middle' });
    el.textContent = TEXT_PLACEHOLDER;
    root.appendChild(el);
    commit(before);
    select([el]);
    setTool('select');
    pendingFocusRef.current = true;
  };
  const addVertex = (p) => {
    const root = rootRef.current;
    let d = draftRef.current;
    if (!d) {
      const before = serialize();
      const el = make(tool, { id: allocId(root), points: pointsAttr([p, p]), fill: 'none', stroke: 'currentColor', 'stroke-width': fmt(defaultStrokeWidth(root)), 'stroke-linejoin': 'round' });
      root.appendChild(el);
      d = { tool, el, points: [p], before };
      draftRef.current = d;
    } else {
      const last = d.points[d.points.length - 1];
      if (Math.hypot(p.x - last.x, p.y - last.y) > gridOf(root) / 4) d.points.push(p);   // 더블클릭의 두 번째 클릭(같은 자리)은 안 넣는다
      d.el.setAttribute('points', pointsAttr([...d.points, p]));
    }
    bump();
  };
  const finishDraft = () => {
    const d = draftRef.current;
    if (!d) return;
    draftRef.current = null;
    if (d.points.length < (d.tool === 'polygon' ? 3 : 2)) { remountFrom(d.before); bump(); return; }
    d.el.setAttribute('points', pointsAttr(d.points));
    commit(d.before);
    select([d.el]);
    setTool('select');
  };

  const moveTo = (g, e) => {
    const grid = gridSnap ? gridOf(rootRef.current) : 0;
    for (const it of g.items) {
      const p = apply(it.inv, e.clientX, e.clientY);
      setTransform(it.el, composeTranslate(it.original, snap(p.x - it.start.x, grid), snap(p.y - it.start.y, grid)));
    }
  };
  const rotateTo = (g, e) => {
    let deg = ((Math.atan2(e.clientY - g.center.y, e.clientX - g.center.x) - g.startAngle) * 180) / Math.PI;
    if (angleSnap) deg = Math.round(deg / ROTATE_STEP) * ROTATE_STEP;
    for (const it of g.items) setTransform(it.el, composeRotate(it.original, deg, it.point.x, it.point.y));
  };
  const clampScale = (s) => {
    if (!Number.isFinite(s)) return 1;
    return Math.abs(s) < MIN_SCALE ? (s < 0 ? -MIN_SCALE : MIN_SCALE) : s;
  };
  const scaleTo = (g, e) => {
    const dx0 = g.startX - g.anchor.x;
    const dy0 = g.startY - g.anchor.y;
    let sx = Math.abs(dx0) > 1 ? (e.clientX - g.anchor.x) / dx0 : 1;
    let sy = Math.abs(dy0) > 1 ? (e.clientY - g.anchor.y) / dy0 : 1;
    if (e.shiftKey) { const s = Math.abs(dx0) > 1 ? sx : sy; sx = s; sy = s; }
    sx = clampScale(sx);
    sy = clampScale(sy);
    for (const it of g.items) setTransform(it.el, composeScale(it.original, sx, sy, it.point.x, it.point.y));
  };
  const drawTo = (g, e) => {
    const p = userPoint(e, g.tool !== 'freehand');
    if (!p) return;
    g.shift = e.shiftKey;
    const { p0, el } = g;
    if (g.tool === 'line' || g.tool === 'arrow') {
      let q = p;
      if (e.shiftKey) {   // 45° 단위
        const len = Math.hypot(p.x - p0.x, p.y - p0.y);
        const ang = Math.round(Math.atan2(p.y - p0.y, p.x - p0.x) / (Math.PI / 4)) * (Math.PI / 4);
        q = { x: p0.x + len * Math.cos(ang), y: p0.y + len * Math.sin(ang) };
      }
      el.setAttribute('x2', fmt(q.x));
      el.setAttribute('y2', fmt(q.y));
    } else if (g.tool === 'rect') {
      let w = p.x - p0.x;
      let h = p.y - p0.y;
      if (e.shiftKey) { const s = Math.max(Math.abs(w), Math.abs(h)); w = (w < 0 ? -1 : 1) * s; h = (h < 0 ? -1 : 1) * s; }
      el.setAttribute('x', fmt(Math.min(p0.x, p0.x + w)));
      el.setAttribute('y', fmt(Math.min(p0.y, p0.y + h)));
      el.setAttribute('width', fmt(Math.abs(w)));
      el.setAttribute('height', fmt(Math.abs(h)));
    } else if (g.tool === 'ellipse') {
      let rx = Math.abs(p.x - p0.x) / 2;
      let ry = Math.abs(p.y - p0.y) / 2;
      let cxv = (p.x + p0.x) / 2;
      let cyv = (p.y + p0.y) / 2;
      if (e.shiftKey) {   // 원: 시작점 모서리에서 지름 2r
        const r = Math.max(rx, ry);
        cxv = p0.x + (p.x < p0.x ? -1 : 1) * r;
        cyv = p0.y + (p.y < p0.y ? -1 : 1) * r;
        rx = r;
        ry = r;
      }
      el.setAttribute('cx', fmt(cxv));
      el.setAttribute('cy', fmt(cyv));
      el.setAttribute('rx', fmt(rx));
      el.setAttribute('ry', fmt(ry));
    } else {
      g.points.push(p);
      el.setAttribute('d', pathData(g.points));
    }
  };
  const finishDraw = (g) => {
    const root = rootRef.current;
    const { el } = g;
    const n = (k) => Number(el.getAttribute(k));
    let keep;
    if (g.tool === 'line' || g.tool === 'arrow') keep = n('x1') !== n('x2') || n('y1') !== n('y2');
    else if (g.tool === 'rect') keep = n('width') > 0 && n('height') > 0;
    else if (g.tool === 'ellipse') keep = n('rx') > 0 && n('ry') > 0;
    else keep = g.points.length > 1;
    if (!keep) { remountFrom(g.before); bump(); return; }   // 크기 0 — 화살표 marker 까지 없던 일로
    let result = el;
    if (g.tool === 'freehand') {
      el.setAttribute('d', pathData(simplify(g.points, viewBoxOf(root)[2] / 300)));
    } else if (g.tool === 'ellipse' && g.shift) {   // Shift 로 그린 원은 <circle>
      result = make('circle', {
        id: el.getAttribute('id'), cx: el.getAttribute('cx'), cy: el.getAttribute('cy'), r: el.getAttribute('rx'),
        fill: 'none', stroke: el.getAttribute('stroke'), 'stroke-width': el.getAttribute('stroke-width'),
      });
      root.insertBefore(result, el);
      el.remove();
    }
    commit(g.before);
    select([result]);
    setTool('select');
  };

  const onPointerDown = (e) => {
    const root = rootRef.current;
    if (e.button !== 0 || gestureRef.current || !root) return;
    stageRef.current.focus({ preventScroll: true });
    const handle = e.target.closest ? e.target.closest('[data-handle]') : null;
    if (handle) { if (!locked) startHandle(handle.getAttribute('data-handle'), e); return; }
    if (tool !== 'select') { if (!locked) startDraw(e); return; }
    const target = selectableTarget(root, e.target);
    if (!target) { if (!e.shiftKey) select([]); return; }
    const sel = selectionRef.current.filter(isConnected);
    const owner = sel.find((s) => s.localName === 'g' && s !== target && s.contains(target));   // 목록에서 고른 그룹의 자식을 누르면 그룹을
    const el = owner || target;
    let next;
    if (e.shiftKey) next = sel.includes(el) ? sel.filter((s) => s !== el) : [...sel, el];
    else next = sel.includes(el) ? sel : [el];
    select(next);
    if (locked || e.shiftKey || !next.length) return;
    startMove(e, next);
  };
  const onPointerMove = (e) => {
    const g = gestureRef.current;
    if (!g) {
      const d = draftRef.current;
      const p = d && userPoint(e, true);
      if (p) d.el.setAttribute('points', pointsAttr([...d.points, p]));
      return;
    }
    if (!samePointer(g, e)) return;
    if (!g.moved && Math.hypot(e.clientX - g.startX, e.clientY - g.startY) < DRAG_THRESHOLD) return;
    g.moved = true;
    if (g.kind === 'move') moveTo(g, e);
    else if (g.kind === 'rotate') rotateTo(g, e);
    else if (g.kind === 'scale') scaleTo(g, e);
    else drawTo(g, e);
    refreshOverlay();
  };
  const onPointerUp = (e) => {
    const g = gestureRef.current;
    if (!g || !samePointer(g, e)) return;
    gestureRef.current = null;
    release(e);
    if (g.kind === 'draw') finishDraw(g);
    else commit(g.before);
  };
  const onDoubleClick = (e) => {
    if (draftRef.current) { e.preventDefault(); finishDraft(); }
  };
  const onKeyDown = (e) => {
    if (e.target !== stageRef.current) return;   // 인스펙터 입력 중엔 손대지 않는다
    const mod = e.ctrlKey || e.metaKey;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if (mod && key === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
    if (mod && key === 'y') { e.preventDefault(); redo(); return; }
    if (key === 'Escape') {
      e.preventDefault();
      if (gestureRef.current) cancelGesture();
      else if (draftRef.current) cancelDraft();
      else if (tool !== 'select') setTool('select');
      else select([]);
      return;
    }
    if (locked || mod || e.altKey) return;
    if (key === 'Delete' || key === 'Backspace') { e.preventDefault(); deleteSelection(); return; }
    const dir = ARROWS[key];
    if (!dir) return;
    e.preventDefault();
    const step = e.shiftKey ? 10 : 1;
    nudge(dir[0] * step, dir[1] * step);
  };
  const onRowClick = (el, additive) => {
    const sel = selectionRef.current.filter(isConnected);
    if (additive) select(sel.includes(el) ? sel.filter((s) => s !== el) : [...sel, el]);
    else select([el]);
    if (stageRef.current) stageRef.current.focus({ preventScroll: true });
  };
  const commitOn = (fn) => ({
    onBlur: (e) => fn(e.currentTarget.value),
    onKeyDown: (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(e.currentTarget.value); } },
  });

  // ── 그리기 ────────────────────────────────────────────────────────
  const root = rootRef.current;
  const h = historyRef.current;
  const sel = root ? selectionRef.current.filter(isConnected) : [];
  const primary = sel[0] || null;
  const rows = root ? listSelectable(root) : [];
  const dirty = !!h && h.dirty;
  const bytes = h ? utf8ByteLength(h.current) : 0;
  const tag = primary ? primary.localName : '';
  const isText = tag === 'text' && sel.length === 1;
  const runs = isText ? textRuns(primary) : [];
  const attr = (name) => (primary ? inheritedAttr(primary, root, name) : '');
  const strokeWidth = attr('stroke-width');
  const fieldKey = `${selectGenRef.current}:${reloadGenRef.current}`;
  const currentTool = TOOLS.find((t) => t.id === tool) || TOOLS[0];
  const resizeOk = !sel.length || canResize(sel);   // 아니면 모서리 핸들을 숨기고 상태줄에 사유(이동·회전은 그대로)
  const swatches = (name, label) => (
    <div className="svg-editor-field">
      <span className="svg-editor-label">{label} <span className="svg-editor-value">{attr(name) || '(기본)'}</span></span>
      <div className="svg-editor-swatches" role="group" aria-label={label}>
        {SWATCHES.map((s) => (
          <button
            key={s.value}
            type="button"
            className={cx('svg-editor-swatch', s.cls, attr(name) === s.value && 'svg-editor-swatch--selected')}
            aria-label={`${label} ${s.label}`}
            aria-pressed={attr(name) === s.value}
            title={s.label}
            onClick={() => setAttr(name, s.value)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="svg-editor-root" data-readonly={locked ? 'true' : 'false'}>
      <div className="svg-editor-toolbar" role="toolbar" aria-label="편집 도구">
        <div className="svg-editor-group" role="group" aria-label="그리기 도구">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={cx('svg-editor-button', tool === t.id && 'svg-editor-button--active')}
              aria-label={t.label}
              aria-pressed={tool === t.id}
              title={`${t.label} — ${t.hint}`}
              disabled={locked && t.id !== 'select'}
              onClick={() => chooseTool(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="svg-editor-group" role="group" aria-label="보조">
          <button type="button" className={cx('svg-editor-button', gridSnap && 'svg-editor-button--active')} aria-label="격자 스냅" aria-pressed={gridSnap} title={`격자 스냅 — 그림 폭의 1/${GRID_DIVISIONS}`} disabled={locked} onClick={() => setGridSnap((v) => !v)}>격자 스냅</button>
          <button type="button" className={cx('svg-editor-button', angleSnap && 'svg-editor-button--active')} aria-label="15도 회전 스냅" aria-pressed={angleSnap} title="회전 핸들을 15° 단위로" disabled={locked} onClick={() => setAngleSnap((v) => !v)}>15° 스냅</button>
        </div>
        <div className="svg-editor-group" role="group" aria-label="이력">
          <button type="button" className="svg-editor-button" aria-label="실행 취소" title="Ctrl+Z" disabled={!h || !h.canUndo()} onClick={undo}>↶ 실행 취소</button>
          <button type="button" className="svg-editor-button" aria-label="다시 실행" title="Ctrl+Y" disabled={!h || !h.canRedo()} onClick={redo}>↷ 다시 실행</button>
          <button type="button" className="svg-editor-button" aria-label="원본으로" title="편집을 모두 버리고 처음 상태로(실행 취소 가능)" disabled={!dirty || locked} onClick={reset}>원본으로</button>
        </div>
        <span className="svg-editor-meta">
          {invalid ? '읽기 전용' : dirty ? '편집본' : '변경 없음'}
          {h ? ` · ${bytes.toLocaleString('en-US')} 바이트` : ''}
        </span>
      </div>

      <div className="svg-editor-workspace">
        {invalid && (
          <div className="svg-editor-readonly" role="alert">
            이 SVG 는 정책을 통과하지 못해 읽기 전용으로 엽니다 — {invalid.reason} ({invalid.code})
          </div>
        )}
        {readOnly && !invalid && <p className="svg-editor-help">읽기 전용 — 고르기만 할 수 있습니다.</p>}
        <div
          ref={stageRef}
          className={cx('svg-editor-stage', tool !== 'select' && 'svg-editor-stage--draw')}
          tabIndex={0}
          role="region"
          aria-label={STAGE_LABEL}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={cancelGesture}
          onDoubleClick={onDoubleClick}
          onKeyDown={onKeyDown}
        >
          <div ref={mountRef} className="svg-editor-mount" />
          {invalid && (
            <div className="svg-editor-mount svg-editor-mount--preview">
              <SvgFigurePreview svgText={svgText} alt="" />
            </div>
          )}
          <div ref={overlayRef} className="svg-editor-selection" aria-hidden="true">
            {HANDLES.filter(([kind]) => kind === 'rotate' || resizeOk).map(([kind, cls]) => <div key={kind} className={cls} data-handle={kind} />)}
          </div>
        </div>
        <p className="svg-editor-help">
          {currentTool.label}: {currentTool.hint} · 방향키 1 · Shift+방향키 10 · Delete 삭제 · Ctrl+Z/Y · Esc
        </p>
        <p className={cx('svg-editor-status', notice && 'svg-editor-status--error')} role="status" aria-live="polite">{notice || (resizeOk ? '' : RESIZE_HINT)}</p>
      </div>

      <aside className="svg-editor-inspector" ref={inspectorRef} aria-label="요소 목록과 속성">
        <h3>요소</h3>
        <ul className="svg-editor-list" aria-label="요소 목록">
          {rows.map(({ el, depth }, i) => (
            <li key={i}>
              <button
                type="button"
                className={cx('svg-editor-list-item', sel.includes(el) && 'svg-editor-list-item--selected')}
                style={{ paddingLeft: 8 + depth * 12 }}
                aria-pressed={sel.includes(el)}
                onClick={(e) => onRowClick(el, e.shiftKey)}
              >
                {rowLabel(el)}
              </button>
            </li>
          ))}
          {!rows.length && <li className="svg-editor-help">요소가 없습니다.</li>}
        </ul>
        {!primary ? (
          <p className="svg-editor-help">캔버스나 목록에서 요소를 고르세요. Shift 로 여러 개를 고릅니다.</p>
        ) : (
          <fieldset className="svg-editor-fieldset" disabled={locked} key={fieldKey}>
            <legend className="svg-editor-kind">{sel.length > 1 ? `${sel.length}개 선택` : rowLabel(primary)}</legend>
            <div className="svg-editor-actions">
              <button type="button" className="svg-editor-button" aria-label="복제" onClick={duplicateSelection}>복제</button>
              <button type="button" className="svg-editor-button" aria-label="삭제" onClick={deleteSelection}>삭제</button>
              <button type="button" className="svg-editor-button" aria-label="맨 앞으로" onClick={() => reorder('front')}>맨 앞으로</button>
              <button type="button" className="svg-editor-button" aria-label="맨 뒤로" onClick={() => reorder('back')}>맨 뒤로</button>
            </div>
            <div className="svg-editor-field">
              <label className="svg-editor-label" htmlFor={`${uid}-angle`}>회전(°) — 선택 중심</label>
              <div className="svg-editor-row">
                <input id={`${uid}-angle`} ref={angleRef} className="svg-editor-input" type="number" step="1" min="-360" max="360" defaultValue="0" inputMode="decimal" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); rotateBy(Number(e.currentTarget.value)); } }} />
                <button type="button" className="svg-editor-button" aria-label="회전 적용" onClick={() => rotateBy(Number(angleRef.current ? angleRef.current.value : 0))}>적용</button>
              </div>
              <div className="svg-editor-row">
                <button type="button" className="svg-editor-button" aria-label="-90도 회전" onClick={() => rotateBy(-90)}>−90°</button>
                <button type="button" className="svg-editor-button" aria-label="+90도 회전" onClick={() => rotateBy(90)}>+90°</button>
              </div>
            </div>
            {isText && (
              <>
                {runs.map((node, i) => (
                  <div className="svg-editor-field" key={i}>
                    <label className="svg-editor-label" htmlFor={`${uid}-run-${i}`}>
                      {runs.length === 1 ? '글자' : `글자 ${i + 1}${node.parentNode.localName === 'tspan' ? ' · tspan' : ''}`}
                    </label>
                    <input id={`${uid}-run-${i}`} className="svg-editor-input" type="text" maxLength={160} defaultValue={node.nodeValue} data-run={i} {...commitOn((v) => setTextRun(node, v))} />
                  </div>
                ))}
                <div className="svg-editor-row">
                  <div className="svg-editor-field">
                    <label className="svg-editor-label" htmlFor={`${uid}-font`}>글자 크기</label>
                    <input id={`${uid}-font`} className="svg-editor-input" type="number" min="1" step="0.5" inputMode="decimal" defaultValue={attr('font-size')} {...commitOn((v) => setNumberAttr('font-size', v, 1))} />
                  </div>
                  <div className="svg-editor-field">
                    <label className="svg-editor-label" htmlFor={`${uid}-anchor`}>정렬</label>
                    <select id={`${uid}-anchor`} className="svg-editor-select" value={attr('text-anchor') || 'start'} onChange={(e) => setAttr('text-anchor', e.target.value)}>
                      {ANCHORS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                    </select>
                  </div>
                </div>
                <label className="svg-editor-check">
                  <input type="checkbox" checked={attr('font-style') === 'italic'} onChange={(e) => setAttr('font-style', e.target.checked ? 'italic' : null)} />
                  기울임
                </label>
              </>
            )}
            {swatches('stroke', '선 색')}
            <div className="svg-editor-row">
              <div className="svg-editor-field">
                <label className="svg-editor-label" htmlFor={`${uid}-width`}>선 굵기</label>
                <input id={`${uid}-width`} className="svg-editor-input" type="number" min="0" step="0.1" inputMode="decimal" defaultValue={strokeWidth} {...commitOn((v) => setNumberAttr('stroke-width', v, 0))} />
              </div>
              <div className="svg-editor-field">
                <label className="svg-editor-label" htmlFor={`${uid}-dash`}>점선</label>
                <select id={`${uid}-dash`} className="svg-editor-select" value={presetOf(attr('stroke-dasharray'), strokeWidth)} onChange={(e) => setDash(e.target.value)}>
                  {DASH_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                  <option value="custom">(직접 지정됨)</option>
                </select>
              </div>
            </div>
            <div className="svg-editor-field">
              <label className="svg-editor-label" htmlFor={`${uid}-cap`}>선 끝</label>
              <select id={`${uid}-cap`} className="svg-editor-select" value={attr('stroke-linecap') || 'butt'} onChange={(e) => setAttr('stroke-linecap', e.target.value)}>
                {LINECAPS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
            {tag !== 'line' && (
              <>
                {swatches('fill', '채움')}
                <div className="svg-editor-field">
                  <label className="svg-editor-label" htmlFor={`${uid}-fill-opacity`}>채움 투명도(0~1)</label>
                  <input id={`${uid}-fill-opacity`} className="svg-editor-input" type="number" min="0" max="1" step="0.1" inputMode="decimal" defaultValue={attr('fill-opacity') || '1'} {...commitOn((v) => setNumberAttr('fill-opacity', v, 0, '1'))} />
                </div>
              </>
            )}
            <p className="svg-editor-help">입력 후 Enter 또는 다른 곳을 누르면 적용됩니다. 그룹에 준 속성은 자식이 따로 지정한 값을 덮지 않습니다.</p>
          </fieldset>
        )}
      </aside>
    </div>
  );
});

export default SvgFigureEditor;
